import { ReactNode } from "react";
import { SEO as Seo } from "./SEO";
import {
  ABOUT_PAGE_SEO_DESCRIPTION,
  ABOUT_PAGE_SEO_TITLE,
  HOME_PAGE_SEO_DESCRIPTION,
  HOME_PAGE_SEO_KEYWORDS,
  HOME_PAGE_SEO_TITLE,
  SIGNUP_PAGE_SEO_DESCRIPTION,
} from "@/lib/seoConfig";

interface PageSEOProps {
  page: "home" | "about" | "contact" | "terms" | "privacy" | "auth" | "signin" | "signup" | "404";
  children?: ReactNode;
  noIndex?: boolean; // Allow override for noIndex
}

const pageConfigs: Record<
  string,
  {
    title: string;
    description: string;
    keywords: string;
    canonical: string;
    structuredData: Array<{ type: string }>;
    noIndex?: boolean;
  }
> = {
  home: {
    title: HOME_PAGE_SEO_TITLE,
    description: HOME_PAGE_SEO_DESCRIPTION,
    keywords: HOME_PAGE_SEO_KEYWORDS,
    canonical: "/",
    structuredData: [{ type: "organization" }, { type: "website" }, { type: "product" }],
  },
  about: {
    title: ABOUT_PAGE_SEO_TITLE,
    description: ABOUT_PAGE_SEO_DESCRIPTION,
    keywords:
      "about TellerWand, family wealth management India, wealth management platform India, financial management software, investment tracking app, Indian finance app, family portfolio management, asset tracking India, personal finance SaaS India, money management platform, financial planning software India",
    canonical: "/about",
    structuredData: [{ type: "organization" }],
  },
  contact: {
    title: "Contact TellerWand | Customer Support & Partnerships",
    description:
      "Get help with investment tracking, expense management, or financial planning. Contact TellerWand support team for technical assistance, partnership inquiries, or feature requests. Quick response guaranteed.",
    keywords:
      "contact TellerWand, TellerWand support India, wealth management support, investment tracker help, financial app customer service, TellerWand partnerships, finance software support India",
    canonical: "/contact-us",
    structuredData: [{ type: "organization" }],
  },
  terms: {
    title: "Terms of Service | TellerWand",
    description:
      "Read TellerWand's terms of service and user agreement for our personal finance management platform.",
    keywords: "TellerWand terms, terms of service, user agreement",
    canonical: "/terms",
    structuredData: [],
  },
  privacy: {
    title: "Privacy Policy | TellerWand",
    description:
      "Learn how TellerWand protects your privacy and handles your financial data securely.",
    keywords: "TellerWand privacy, privacy policy, data protection",
    canonical: "/privacy",
    structuredData: [],
  },
  auth: {
    title: "Authentication | TellerWand",
    description:
      "Sign in to your TellerWand account to access your personal finance dashboard and wealth tracking tools.",
    keywords: "TellerWand login, sign in, authentication",
    canonical: "/auth",
    structuredData: [],
    noIndex: true,
  },
  signin: {
    title: "Login | Access Your Wealth Dashboard & Portfolio",
    description:
      "Login to TellerWand to view your investment portfolio, track expenses, manage family finances, and monitor PPF, EPF, NPS, mutual funds & more. Secure access to your financial data.",
    keywords:
      "TellerWand login, sign in wealth tracker, investment tracker login, portfolio login India, financial app login, wealth management login, expense tracker sign in",
    canonical: "/auth",
    structuredData: [],
    noIndex: true,
  },
  signup: {
    title: "Sign Up Free | Start Tracking Your Wealth & Investments Today",
    description: SIGNUP_PAGE_SEO_DESCRIPTION,
    keywords:
      "sign up TellerWand, create free account, free wealth tracker India, free investment app, free expense tracker, register TellerWand, free financial planning app India, wealth management free trial, investment tracker sign up",
    canonical: "/auth",
    structuredData: [],
    noIndex: true,
  },
  "404": {
    title: "Page Not Found | TellerWand",
    description:
      "The page you're looking for doesn't exist. Return to TellerWand to manage your personal finances.",
    keywords: "404, page not found, TellerWand",
    canonical: "/404",
    structuredData: [],
    noIndex: true,
  },
};

/**
 * Renders the SEO component with specified page configurations and children.
 * @example
 * PageSEO({ page: 'home', children: <div>Content</div> })
 * <SEO title="Home" description="Welcome to our homepage" keywords="home, main" canonical="https://example.com/home" structuredData={structuredDataObject}><div>Content</div></SEO>
 * @param {Object} params - Parameters containing page and children details.
 * @param {string} params.page - The key representing the page for which SEO configuration is retrieved.
 * @param {React.ReactNode} params.children - Elements to be rendered inside the SEO component.
 * @returns {JSX.Element} A JSX element representing the SEO configuration applied to the children.
 */
export const PageSEO = ({ page, children, noIndex }: Readonly<PageSEOProps>) => {
  const config = pageConfigs[page];

  return (
    <Seo
      title={config.title}
      description={config.description}
      keywords={config.keywords}
      canonical={config.canonical}
      structuredData={config.structuredData as Array<{ type: string; data?: unknown }>}
      noIndex={noIndex ?? config.noIndex ?? false}
    >
      {children}
    </Seo>
  );
};

export default PageSEO;
